// @givmo/board — the schedule dialog's Invitations section (CBG-CAL1 slice 1d; the approved frames
// `schedule-sender`, `-sender-picker`, `-connect-inline`, `-fallback`, `-download-only`).
//
// The sender Board resolved for this officer (`GET …/calendar/senders`, D-CBG-54's order: a connected
// board account, else their own calendar, else email from Givmo), a way to pick another (their own
// calendar, while the board account sends, is their choice for this one meeting), and "Connect your
// calendar" inline when they have none of their own (`can_connect`) and no board account sends: while
// one does, connecting their own wouldn't make the meetings go out from them, so the line would be
// untrue (CBG-UX14). With no channel at all the section is absent and the dialog is today's. "Don't
// send invitations" is always a choice once a sender exists, so not sending is the officer's own act
// too (plan §3.6).
//
// The officer's own calendar that isn't connected is missing from the senders read (README D-5), so
// core's list is read beside it to say "Reconnect" rather than "Connect" (B5): Google stopped letting
// Board use it, or core keeps it `organizer_left` after its owner regained access. Either way it is
// reconnected, never connected again: core refuses a second personal calendar while one is live.

import { useState } from 'react';
import type { BoardCalendarSendersOut, OrgIntegrationConnectionOut } from '@givmo/api-client';
import { Button, RadioTile, RadioTileGroup } from '@givmo/design-system';
import { calendarWords as w } from '../../calendar/calendar-words';

const FIELD_LABEL = 'font-sans text-s12 font-w600 tracking-givmo text-black';
const LINE = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';

/** A choice the section offers: one of the backend's senders, or not sending. */
export type SendChoice = 'my_calendar' | 'board_account' | 'givmo_email' | 'none';

/** Whether the senders read leaves the officer anything to do here: a sender, or a calendar to connect. */
export function offersInvitations(senders: BoardCalendarSendersOut | null | undefined): boolean {
  return (senders?.senders ?? []).length > 0 || senders?.can_connect === true;
}

/** The sender the section starts on: the backend's resolved default, or not sending. */
export function defaultChoice(senders: BoardCalendarSendersOut | null | undefined): SendChoice {
  const offered = senders?.default_send_from;
  return offered === 'my_calendar' || offered === 'board_account' || offered === 'givmo_email'
    ? offered
    : 'none';
}

/** Whether a choice sends from a connected Google calendar (so an empty video link becomes Meet). */
export function sendsFromGoogle(
  senders: BoardCalendarSendersOut | null | undefined,
  choice: SendChoice,
): boolean {
  if (choice !== 'my_calendar' && choice !== 'board_account') return false;
  const sender = (senders?.senders ?? []).find((row) => row.send_from === choice);
  return sender !== undefined && (sender.provider ?? 'google_calendar') === 'google_calendar';
}

export function InvitationsSection({
  senders,
  ownConnection,
  ownConnectionKnown,
  orgName,
  choice,
  onChoice,
  onConnect,
}: {
  senders: BoardCalendarSendersOut;
  /** The officer's own connection from core's list, when they have one (any status). */
  ownConnection: OrgIntegrationConnectionOut | undefined;
  /**
   * Core's list has answered (or is not served), so "Connect" vs "Reconnect" is known. Until then
   * the line waits rather than offer the wrong act.
   */
  ownConnectionKnown: boolean;
  orgName: string;
  choice: SendChoice;
  onChoice: (next: SendChoice) => void;
  /** Open the connect dialog (or reconnect) for the officer's own calendar. */
  onConnect: () => void;
}) {
  const rows = senders.senders ?? [];
  const mine = rows.find((row) => row.send_from === 'my_calendar');
  const board = rows.find((row) => row.send_from === 'board_account');
  const givmo = rows.find((row) => row.send_from === 'givmo_email');
  const [picking, setPicking] = useState(false);
  // D-CBG-54: while the board account sends, one's own calendar is a choice per meeting, so the
  // inline line ("… to send them from you.") is offered only while no board account is offered.
  const boardSends = board !== undefined;
  const needsReconnect =
    !boardSends &&
    mine === undefined &&
    ownConnection !== undefined &&
    ownConnection.status !== 'connected';
  const offersConnect = !boardSends && mine === undefined && senders.can_connect === true;

  const resolved =
    choice === 'my_calendar' && mine !== undefined ? (
      <>
        {w.fromMine}, <span className="font-w600 text-text-strong">{mine.organizer_email}</span>
      </>
    ) : choice === 'board_account' && board !== undefined ? (
      <>
        {w.fromBoard}, <span className="font-w600 text-text-strong">{board.organizer_email}</span>
      </>
    ) : choice === 'givmo_email' && givmo !== undefined ? (
      w.fromGivmo(orgName)
    ) : (
      w.notSentLine
    );

  return (
    <div
      className="flex flex-col gap-8 border-t border-border-subtle pt-14"
      data-testid="meeting-invitations-section"
    >
      <span className={FIELD_LABEL}>{w.invitationsLabel}</span>
      {picking ? (
        <RadioTileGroup
          value={choice}
          onValueChange={(next) => onChoice(next as SendChoice)}
          aria-label={w.pickerLabel}
          testId="meeting-invitations-picker"
        >
          {mine !== undefined ? (
            <RadioTile
              value="my_calendar"
              label={w.pickMine}
              description={mine.organizer_email ?? ''}
            />
          ) : null}
          {board !== undefined ? (
            <RadioTile
              value="board_account"
              label={w.pickBoard}
              description={board.organizer_email ?? ''}
            />
          ) : null}
          {givmo !== undefined ? (
            <RadioTile
              value="givmo_email"
              label={w.pickGivmo}
              description={w.pickGivmoDescription(orgName)}
            />
          ) : null}
          <RadioTile value="none" label={w.pickNone} />
        </RadioTileGroup>
      ) : (
        <div className="flex flex-col items-start gap-4">
          <p className={LINE} data-testid="meeting-invitations-sender">
            {resolved}
          </p>
          {/* What the fallback does sits right under its line, before the act that changes it. */}
          {choice === 'givmo_email' && givmo !== undefined ? (
            <p className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              {w.givmoNote}
            </p>
          ) : null}
          {rows.length > 0 ? (
            <Button
              appearance="saas"
              tone="link"
              size="md"
              onClick={() => setPicking(true)}
              testId="meeting-invitations-change"
            >
              {rows.length > 1 ? w.sendFromAnother : w.change}
            </Button>
          ) : null}
        </div>
      )}
      {ownConnectionKnown && (offersConnect || needsReconnect) ? (
        <p className={LINE} data-testid="meeting-invitations-connect">
          <Button
            appearance="saas"
            tone="link"
            size="md"
            className="align-baseline"
            onClick={onConnect}
            testId="meeting-invitations-connect-button"
          >
            {needsReconnect ? w.reconnectInline : w.connectInline}
          </Button>{' '}
          {w.connectInlineTail}
        </p>
      ) : null}
    </div>
  );
}
